import { supabase } from './client';

/**
 * One round trip for everything the shell needs at boot, served by the
 * `core.my_session` view. RLS scopes it to the caller, so there is no user id to
 * pass and no way to ask for somebody else's.
 */
export interface SessionProfile {
  userId: string;
  username: string;
  displayName: string | null;
  hospital: string | null;
  section: string | null;
  title: string | null;
  mustResetPassword: boolean;
  rolesByModule: Partial<
    Record<'certifications' | 'purchasing' | 'attendance' | 'devices', string[]>
  >;
}

interface MySessionRow {
  user_id: string;
  username: string;
  display_name: string | null;
  hospital: string | null;
  section: string | null;
  title: string | null;
  must_reset_password: boolean;
  roles_by_module: Record<string, string[]> | null;
}

export async function fetchSessionProfile(): Promise<SessionProfile | null> {
  const { data, error } = await supabase()
    .from('my_session')
    .select('*')
    .maybeSingle<MySessionRow>();

  if (error) {
    // A signed-in user with no profile row is a data problem, not an auth one —
    // surface it rather than silently signing them out.
    console.error('could not load session profile:', error.message);
    return null;
  }
  if (!data) return null;

  return {
    userId: data.user_id,
    username: data.username,
    displayName: data.display_name,
    hospital: data.hospital,
    section: data.section,
    title: data.title,
    mustResetPassword: data.must_reset_password,
    rolesByModule: (data.roles_by_module ?? {}) as unknown as SessionProfile['rolesByModule'],
  };
}

/** Lookup lists for a module's dropdowns, from core.attributes. */
export async function fetchAttributes(
  module: 'certifications' | 'purchasing' | 'attendance' | 'devices',
  key: string,
): Promise<string[]> {
  const { data, error } = await supabase()
    .from('attributes')
    .select('name')
    .eq('module', module)
    .eq('key', key)
    .order('name')
    .returns<{ name: string }[]>();

  if (error) {
    console.error('could not load attributes:', error.message);
    return [];
  }
  return (data ?? []).map(row => row.name);
}
